// src/App.js
import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Container, Row, Col, Navbar, Button, Image, Modal, Form, Alert } from "react-bootstrap";
import SearchForm from "./components/SearchForm";
import ResultsPanel from "./components/ResultsPanel";
import { queryOpenAI } from "./services/openai";
import { safeParseNumber, DEFAULT_TOTAL_ANCESTORS } from "./utils/estimates";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import DiaryScreen from "./components/DiaryScreen";
import AboutEnergyPools from "./components/AboutEnergyPools";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { doc, setDoc, updateDoc, arrayUnion, getDoc, collection, getDocs, deleteDoc, onSnapshot, serverTimestamp, runTransaction } from "firebase/firestore";
import { db } from "./firebase";
import { signInWithGoogle, signOut as firebaseSignOut, auth } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";

import mixpanel from 'mixpanel-browser';

mixpanel.init("2ab96ecad7451ea49cebc5f84b528f7c", {  autocapture:true, track_pageview: true});
dayjs.extend(relativeTime);



/*
 Prompt strategy:
 - Ask the model to compute counts for:
    * withTrait (how many ancestors had that trait at all)
    * goodAt (how many were good)
    * excellentAt (how many exceptional)
 - Return the answer in pure JSON with the fields:
    { totalAncestors, withTrait, goodAt, excellentAt, percentages: { withTrait, good, excellent }, reasoning: "..." }
 - Use temperature 0.0 to encourage deterministic outputs.
*/

export default function App() {
  const [loading, setLoading] = useState(false);
  const [responseRaw, setResponseRaw] = useState("");
  const [error, setError] = useState(null);
  const [queryMeta, setQueryMeta] = useState({ query: "", totalAncestors: DEFAULT_TOTAL_ANCESTORS, model: "gpt-4o-mini" });
  const [parsed, setParsed] = useState(null);
  // Firebase auth user
  const [user, setUser] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userId, setUserId] = useState(null);

  const [userPools, setUserPools] = useState([]);
  const [loadingPools, setLoadingPools] = useState(true);

  const [aboutClicked, setAboutClicked] = useState(false);


  // credits
  const [credits, setCredits] = useState(null);
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [customAmount, setCustomAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("stripe"); // "stripe" or "paystack"
  const [charged, setCharged] = useState(false);
  const PRICE_PER_POOL = 10;

  const [resultsResetTrigger, setResultsResetTrigger] = useState(0);





  const mockPools = [ 
  
  {
  id: "1",
  query: "Calm under pressure",
  goodAt: 3750000,
  excellentAt: 750000,
  createdAt: "2025-01-10",
  reasoning: "The trait of being 'calm under pressure' is generally classified as a personality trait or psychological tendency, which is influenced by both genetic and environmental factors. Evidence suggests that many individuals display varying degrees of calmness in stressful situations, likely attributable to natural temperament, learned coping mechanisms, and life experiences. Based on normal distribution in the population, we estimate that approximately 25% of ancestors could be considered good at this trait, with 5% reaching a level of excellence. This conservative estimate accounts for the diverse factors that influence personality traits across different regions and cultures."
  },
  
  //  {
  // id: "3",
  // query: "My friend falling for me",
  // goodAt: 4500000,
  // excellentAt: 150000,
  // createdAt: "2025-09-10",
  // reasoning: "The ancestors represented here were exceptionally skilled at building and maintaining social bonds, which includes fostering love in friendships. Throughout history, many cultures placed great emphasis on communal relationships, suggesting that the ability to manifest love and loyalty was a valued trait. This includes emotional intelligence and the capacity for empathy, which are traits that can be cultivated over generations. The estimates are conservative as they consider the natural variability of such traits across different ancestral lines and regions, acknowledging that while many may have shown this capability, only a select few reached an extraordinary level of success in doing so, hence the lower 'Top 1%' count."
  // },
  {
  id: "2",
  query: "Creative imagination",
  goodAt: 3750000,
  excellentAt: 150000,
  createdAt: "2025-01-18",
  reasoning:"Creative imagination is widely recognized as a psychological tendency that can exist in varying degrees among individuals. Drawing upon population distributions and cognitive traits, it is reasonable to estimate that approximately 25% of individuals exhibit strong imaginative capabilities, with around 1% demonstrating exceptional levels. This reflects common trends found in various fields such as art, science, and literature, indicating that many individuals throughout history have possessed this trait. The numbers are conservative given that creativity can be nurtured and developed, and it recognizes that not all ancestors would have been able to manifest their creativity in profound ways."
  },
  {
  id: "3",
  query: "Public speaking",
  goodAt: 3750000,
  excellentAt: 750000,
  createdAt: "2025-02-01",
  reasoning:"Public speaking is a skill that can be developed through practice and social interaction, affecting many facets of communication throughout history. Given the importance of oratory in leadership, education, and societal development, it's reasonable to estimate that around 25% of ancestors would have achieved a strong ability, with 5% excelling in it. This estimate considers the historical significance of speech and persuasion in various cultures. The results are conservative, reflecting that while many may have had the skill, only a minority would be truly exceptional at it."
  },
  {
  id: "4",
  query: "Unexplained improved health",
  goodAt: 3000000,
  excellentAt: 100000,
  createdAt: "2025-11-10",
  reasoning: "Throughout history, many cultures have believed in the power of faith, rituals, and healing practices to achieve remarkable health outcomes, suggesting that a subset of ancestors may have experienced these extraordinary outcomes. The estimates provided reflect the possibility that, within the broad swath of human existence, about 20% could have had strong experiences or practices in this regard. The figure for 'Top 1%' is conservative as it reflects those exhibiting particularly remarkable cases of health improvement, potentially influenced by religious or spiritual belief systems. These beliefs often posit that a strong conviction or certain rituals could yield unexpectedly positive health results, hence the conservative nature of the estimates aligns with documented instances where such beliefs intersect with health outcomes."
  },
  ];


   useEffect(() => {
    if (user?.uid) {
      const userRef = doc(db, "pool_users", user.uid);
      const unsubscribe = onSnapshot(userRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          setCredits(docSnapshot.data().credits || 0);
        }
      });
      return () => unsubscribe();
    }
  }, [user]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setUser(null);
        setUserId(null);
        return;
      }

      // 🔥 SYNC USER TO FIRESTORE (FIRST TIME / RETURNING)
      await syncUserToFirestore(u);

      // 🔹 Update UI state
      setUser({
        uid: u.uid,
        displayName: u.displayName,
        email: u.email,
        photoURL: u.photoURL,
      });
      setUserId(u.uid);
    });

    return unsubscribe;
  }, []);



async function syncUserToFirestore(user) {
  if (!user) return;

  const userRef = doc(db, "pool_users", user.uid);
  const snap = await getDoc(userRef);

  if (!snap.exists()) {
    // ✅ FIRST TIME SIGN-IN
    await setDoc(userRef, {
      uid: user.uid,
      username: user.displayName || "Anonymous",
      email: user.email,
      credits: 1.5,
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp(),
    });
  } else {
    // ✅ RETURNING USER
    await updateDoc(userRef, {
      lastLogin: serverTimestamp(),
    });
  }
}

  // auth actions
  async function handleSignIn() {
    try {
      await signInWithGoogle();
      // onAuthStateChanged will update state
    } catch (err) {
      console.error("Google sign-in error:", err);
      setError("Sign-in failed");
    }
  }

  async function handleSignOut() {
    try {
      await firebaseSignOut();
      // onAuthStateChanged will set user to null
    } catch (err) {
      console.error("Sign-out error:", err);
      setError("Sign-out failed");
    }
  }

  useEffect(() => {
  if (!userId) {
    setUserPools([]);
    return;
  }

  async function fetchPools() {
  setLoadingPools(true);

  try {
    if (!userId) {
      setUserPools([]);
      return;
    }

    // Reference: pools/{userId}/userPools
    const colRef = collection(db, "pools", userId, "userPools");

    const snap = await getDocs(colRef);

    const pools = snap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    setUserPools(pools);
  } catch (err) {
    console.error("Error loading user pools:", err);
    setUserPools([]);
  } finally {
    setLoadingPools(false);
  }
}

  fetchPools();
}, [userId]);


  async function detectRegions(rawInput, model) {
    const classifierPrompt = `
      You are an ancestral region classifier.

      The user provided these ancestral groups, languages or locations:
      "${rawInput}"

      Return ONLY raw JSON. 
      NO markdown.
      NO code fences.
      NO explanations.
      NO backticks.

      The JSON MUST look exactly like this:

      {
        "specificInputs": ["string", "string"],
        "detectedRegions": ["string", "string"]
      }

      Rules:
      - "specificInputs": up to 6 trimmed tokens found in the user’s input.
      - "detectedRegions": broad ancestral regions such as "West Africa", "East Africa", "South Asia", "Europe", "Middle East", "East Asia", "Native America", "Polynesia".
      - If unsure, return an empty array.
      - Output must be VALID JSON and NOTHING else.
      `;
        // console.log("detectRegions prompt:", classifierPrompt, model);
        const res = await queryOpenAI(classifierPrompt, model);
        // console.log("detectRegions result:", res);
        return JSON.parse(res);
      }


  async function handleSubmit({ query, totalAncestors, model, ancestralInputs }) {
    setError(null);
    setParsed(null);
    setResponseRaw("");
    setLoading(true);
    setQueryMeta({ query, totalAncestors, model });

    try {
      // 🔒 DEDUCT CREDIT FIRST
      if (!userId) {
        throw new Error("Please sign in to continue");
      }

      await deductOneCredit(userId);

      // Step 1 — detect regions
      const regionData = await detectRegions(ancestralInputs, model);

      const prompt = buildPrompt(
        query,
        totalAncestors,
        regionData.detectedRegions,
        regionData.specificInputs
      );

      const content = await queryOpenAI(prompt, model);

      setResponseRaw(content);
      setParsed(content);

      // ---- SAVE RECONSTRUCTED POOL TO FIRESTORE ---- //
      try {
        let data = null;

        try {
          data = JSON.parse(content);
        } catch {
          const match = content.match(/\{[\s\S]*\}$/);
          if (match) data = JSON.parse(match[0]);
        }

        if (data) {
          const reconstructed = {
            query: data.trait || queryMeta.query,
            totalAncestors: data.totalAncestors,
            trait: data.trait,
            traitRemix: data.traitRemix,
            goodAt: data.goodAt,
            excellentAt: data.excellentAt,
            percentages: data.percentages,
            ancestryUsed: data.ancestryUsed,
            reasoning: data.reasoning,
            createdAt: new Date().toISOString(),
          };

          const poolId = crypto.randomUUID();
          const ref = doc(db, "pools", userId, "userPools", poolId);

          await setDoc(ref, reconstructed);
        }
      } catch (err) {
        console.error("Error saving pool:", err);
      }

    } catch (err) {
      console.error(err);
      setParsed(null);

      // 🛑 Friendly credit error
      if (err.message === "No credits remaining") {
        setShowRechargeModal(true)
        return
        // setError("You’ve run out of credits. Please upgrade your plan.");
      } else {
        setError(err.message || String(err));
      }

    } finally {
      setLoading(false);
    }
  }



  async function deductOneCredit(userId) {
    if (!userId) throw new Error("Not signed in");

    const userRef = doc(db, "pool_users", userId);

    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(userRef);

      if (!snap.exists()) {
        throw new Error("User record not found");
      }

      const currentCredits = snap.data().credits ?? 0;

      if (currentCredits <= 0) {
        
        throw new Error("No credits remaining");
      }

      transaction.update(userRef, {
        credits: currentCredits - 0.5,
      });
    });
  }


  // Drill function: supports 'good' (ask: of those with trait, how many were good?), 'excellent' (of good how many excellent)
  // async function handleDrill(step) {
  //   // parse previous
  //   let parsed = null;
  //   try {
  //     parsed = JSON.parse(responseRaw);
  //   } catch (e) {
  //     const match = responseRaw && responseRaw.match(/\{[\s\S]*\}$/);
  //     if (match) {
  //       try { parsed = JSON.parse(match[0]); } catch (e2) { parsed = null; }
  //     }
  //   }
  //   if (!parsed) {
  //     setError("Cannot drill: previous response could not be parsed into JSON.");
  //     return;
  //   }

  //   setLoading(true);
  //   setError(null);

  //   // Build second-level prompt:
  //   // If step === 'good' => ask "Of those counted in withTrait, estimate how many would be 'good' and 'excellent' etc."
  //   let nextPrompt = "";
  //   if (step === "good") {
  //     nextPrompt = `You are given: totalAncestors=${parsed.totalAncestors}, estimated_with_trait=${parsed.withTrait}. 
  //   Provide a JSON with fields:
  //   {
  //     "context": "drill_good",
  //     "basePopulation": ${parsed.withTrait},
  //     "goodAt": <number>,
  //     "excellentAt": <number>,
  //     "percentages": { "good": <percent>, "excellent": <percent> },
  //     "reasoning": "<short reasoning>"
  //   }
  //   Use evidence and rough population distributions. Keep numbers integers.`;
  //       } else if (step === "excellent") {
  //         // Drill from 'goodAt' if available
  //         const base = parsed.goodAt ?? Math.round((parsed.withTrait || 0) * 0.25);
  //         nextPrompt = `You are given: base=${base} (interpreted as those who are GOOD). 
  //   Provide a JSON with fields:
  //   {
  //     "context":"drill_excellent",
  //     "base": ${base},
  //     "excellentAt": <number>,
  //     "percentages": { "excellent": <percent> },
  //     "reasoning": "<short reasoning>"
  //   }
  //   Keep answers integers and concise.`;
  //   }

  //   try {
  //     const content = await queryOpenAI(nextPrompt, queryMeta.model);
  //     setResponseRaw(content); // replace previous response with the result of the drill
  //   } catch (err) {
  //     console.error(err);
  //     setError(err.message || String(err));
  //   } finally {
  //     setLoading(false);
  //   }
  // }

  const loadPool = (pool) => {
  const reconstructed = {
    totalAncestors: DEFAULT_TOTAL_ANCESTORS,
    trait: pool.query,
    traitRemix: pool.query,
    goodAt: pool.goodAt,
    excellentAt: pool.excellentAt,
    percentages: {
      good: Math.round((pool.goodAt / DEFAULT_TOTAL_ANCESTORS) * 100),
      excellent: Math.round((pool.excellentAt / DEFAULT_TOTAL_ANCESTORS) * 100),
    },
    ancestryUsed: {
      specific: [],
      regions: [],
    },
    reasoning: pool.reasoning || "No reasoning provided.",
  };

  // THE IMPORTANT PART:
  // ⚠️ ResultsPanel expects a JSON STRING in responseRaw
  const jsonString = JSON.stringify(reconstructed);

  setResponseRaw(jsonString);   // let ResultsPanel parse normally
  setParsed(jsonString);        // maintain compatibility

  // Reset the aboutClicked state
  setAboutClicked(false);
  localStorage.setItem("aboutResultOpened", "false");

  // Scroll into view
  setTimeout(() => {
    const el = document.getElementById("results-section");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  }, 120);
};

const handleCheckout = async (amount) => {
    try {
      if (amount <= 7.0) {
        alert("Amount must be greater than 6.999");
        return;
      }

      setError("")

      const platformFeeRate = 0.00;
    

      // Add your 10% platform fee
      let totalAmount = amount * (1 + platformFeeRate);


      const endpoint = paymentMethod === "paystack"
        ? "https://www.ancestralpool.com/api/paystackpayments"
        : "https://www.ancestralpool.com/api/payments";

      if (paymentMethod == "paystack") {
        // convert to naira for paystack
        totalAmount = totalAmount * 2000
      }


      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: totalAmount, myamount: amount, userId: user?.uid }),
      });
     
      // Check response once
        if (!response.ok) {
          const errorText = await response.text(); // only read once
          console.error("Server returned error:", errorText);
          throw new Error(`Server Error: ${response.status} ${errorText}`);
        }

        const data = await response.json();


      if (!data.url) {
        // console.log("Response from backend:", data);
        throw new Error("No URL returned from backend");
      }

      window.location.href = data.url;
    } catch (error) {
      // mixpanel.track('handleCheckOutError', {
      //   error: error?.message,
      //   user: user?.uid
      // })
      console.error("Checkout error:", error);
    }
  };


const allPools = [...userPools, ...mockPools];

 return (
   <Router>
      <Routes>
      <Route path="/about-energy-pools" element={<AboutEnergyPools />} />

        {/* HOME PAGE */}
        <Route
          path="/"
          element={
  <>
    {/* NAVBAR */}
    <Navbar
      expand="lg"
      bg="white"
      style={{
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        padding: "14px 20px",
        position: "sticky",
        top: 0,
        zIndex: 1000,
      }}
    >
      <Navbar.Brand
        href="#"

        style={{
          fontWeight: 500,
          fontSize: "18px",
          letterSpacing: "-0.5px",
          background: "linear-gradient(90deg, #0e1212, #1a1f1a)",
          WebkitBackgroundClip: "text",
          color: "transparent",
          textShadow: "0px 1px 1px rgba(0,0,0,0.2)",
        }}
      >
      <img
        src="/logo192.png"
        alt="AncestralPoolLogo"
        style={{ width: "36px", height: "28px", marginRight: "0px", verticalAlign: "middle" }}
      />AncestralPool
      </Navbar.Brand>

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>     
                <div style={{ fontSize: 14, color: "#444" }}>
                  {user?.photoURL ? ( <Image style={{marginRight:"10px"}} src={user?.photoURL} roundedCircle width={34} height={34} /> ) : ( <div style={{ width: 34, height: 34, borderRadius: 999, background: "#eee", display: "flex", alignItems: "center", justifyContent: "center", marginRight:"10px"}}> {user?.displayName?.[0]?.toUpperCase() ?? " "} </div> )}                      
                </div>
        </div>
            
     
      

      {/* Drawer Overlay */}
      <div
        onClick={() => setDrawerOpen(false)}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: drawerOpen ? "100vw" : "0",
          height: "100vh",
          background: "rgba(0,0,0,0.4)",
          transition: "0.3s",
          opacity: drawerOpen ? 1 : 0,
          pointerEvents: drawerOpen ? "auto" : "none",
          zIndex: 999,
        }}
      />

      {/* DRAWER */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: drawerOpen ? 0 : "-300px",
          height: "100vh",
          width: "260px",
          background: "#fff",
          boxShadow: "0 0 14px rgba(0,0,0,0.2)",
          zIndex: 1000,
          padding: "24px",
          transition: "0.3s",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
               

        {/* Auth actions */}
        {user ? (
          <>

            <Link
              onClick={() => setDrawerOpen(false)}
              style={{
                  textDecoration: "none",
                  color: "#444",
                  fontSize: "14px",
                  fontWeight: 600,
                }}
            >

              {/* 🔹 Show Credit only when logged in */}
                {user && (
                  <Button variant="light"
                    onClick={() => setShowRechargeModal(true)}
                  >
                    <span style={{ color: "grey", marginRight: "5px" }}>
                    {credits === null ? "Loading..." : credits.toFixed(2)}
                  </span>
                  Credits
                  </Button>
                )}

            </Link>

            <Link
              to="/diary"
              onClick={() => setDrawerOpen(false)}
              style={{
                textDecoration: "none",
                color: "#444",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              {/* <img src="/event_244.png" alt="diary" style={{ width: "28px", marginBottom: "4px", marginRight: "6px"}} />  */}
              My Ancestral Journal 
            </Link>

            <Link
              to="/about-energy-pools"
              onClick={() => setDrawerOpen(false)}
              style={{
                  textDecoration: "none",
                  color: "#444",
                  fontSize: "14px",
                  fontWeight: 600,
                }}
            >

              About Energy Pools (Q&A)

            </Link>

            <button
              className="btn btn-outline-dark"
              style={{ marginTop: "auto",
              background: "linear-gradient(120deg, #0dc300ff, #065600ff)",
              color: "white",
              border: "none" }}
              onClick={() => {
                handleSignOut();
                setDrawerOpen(false);
              }}
            >
              Sign Out
            </button>
          </>
        ) : (
          <>
          <Link
              to="/about-energy-pools"
              onClick={() => setDrawerOpen(false)}
              style={{
                  textDecoration: "none",
                  color: "#444",
                  fontSize: "14px",
                  fontWeight: 600,
                }}
            >

              About Energy Pools (Q&A)

            </Link>
            
            <button
              className="btn btn-dark"
              style={{ 
              background: "linear-gradient(120deg, #0dc300ff, #065600ff)",
              color: "white",
              border: "none" }}
              onClick={() => {
                handleSignIn();
                setDrawerOpen(false);
              }}
            >
              Sign In with Google
            </button>
          </>
        )}
      </div>

      <button className="btn btn-outline-secondary" onClick={() => setDrawerOpen(true)} > ☰ </button>

    </Navbar>

    {/* MAIN CONTENT */}
    <Container
      fluid
      style={{
        paddingTop: "40px",
        paddingBottom: "80px",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "center",
        textAlign: "center",
        maxWidth: "650px",
      }}
    >
      
      {/* DESCRIPTION */}
      {/* <p
        style={{
          marginTop: "10px",
          marginBottom: "26px",
          color: "#555",
          fontSize: "15.5px",
          lineHeight: "1.6",
          maxWidth: "400px",
        }}
      >
       
        Discover how many of your ancestors possessed those traits you want. Explore Your Ancestral Power.
      </p> */}

      {/* FORM */}
      <div style={{ width: "100%", maxWidth: "420px" }}>
        <SearchForm onSubmit={handleSubmit} userId={userId}/>
      </div>

      {/* SAVED POOLS */}
<div style={{ width: "100%", maxWidth: "760px", marginTop: "48px" }}>
  {/* <h5
    style={{
      fontWeight: 700,
      fontSize: "16px",
      marginBottom: "20px",
      color: "#333",
      lineHeight: 1.4,
      letterSpacing: "-0.5px",
      textAlign: "left",
    }}
  >
    Continue...
  </h5> */}

  <div
    style={{
      display: "flex",
      overflowX: "auto",
      gap: "10px",
      paddingBottom: "14px",
    }}
    onWheel={(e) => {
            e.currentTarget.scrollLeft += e.deltaY;
          }}
  >
    <style>
      {`
        .pool-card:hover .delete-btn {
          opacity: 1;
          transform: scale(1.1);
        }
      `}
    </style>

    {allPools.map((pool, i) => {
      const isMock = mockPools.some(mp => mp.id === pool.id);

      const handleDelete = async (e) => {
        e.stopPropagation();

        if (isMock) {
          alert("This is a default pool and cannot be deleted.");
          return;
        }

        if (!userId) return;

        try {
          // DELETE: pools/{userId}/userPools/{pool.id}
          await deleteDoc(doc(db, "pools", userId, "userPools", pool.id));

          // remove from UI
          setUserPools(prev => prev.filter(p => p.id !== pool.id));
        } catch (err) {
          console.error("Error deleting pool:", err);
        }
      };

      return (
        <div
          key={pool.id}
          onClick={() => {
            loadPool(pool);
            // update a reset trigger for ResultsPanel
            setResultsResetTrigger(Date.now());
          }}
          className="pool-card"
          style={{
            minWidth: "300px",
            background: "linear-gradient(135deg, #ffffff, #f8f9fa)",
            padding: "22px",
            borderRadius: "20px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
            border: "1px solid #e0e0e0",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            cursor: "pointer",
            position: "relative",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
          }}
          
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-6px)";
            e.currentTarget.style.boxShadow = "0 16px 40px rgba(0,0,0,0.12)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0px)";
            e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,0,0,0.08)";
          }}
        >
          <button
            onClick={handleDelete}
            className="delete-btn"
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              border: "none",
              background: "rgba(255,255,255,0.9)",
              color: "#686868ff",
              fontSize: "18px",
              borderRadius: "50%",
              width: "28px",
              height: "28px",
              cursor: "pointer",
              opacity: 0.7,
              transition: "all 0.2s ease",
            }}
            title="Delete pool"
          >
            ✕
          </button>

          <div style={{ marginBottom: "0px" }}>
            {/* <div
              style={{
                fontSize: "12px",
                fontWeight: 600,
                color: "#6b7280",
                marginBottom: "4px",
                letterSpacing: "-0.2px",
              }}
            >
              Your Ancestors Who Manifested
            </div> */}
            <div
              style={{
                fontSize: "16px",
                fontWeight: 800,
                lineHeight: 1.3,
                color: "#111",
                letterSpacing: "-0.3px",
                marginTop: "26px",
              }}
            >
              {`${pool.query} `.replace(/\b\w/g, c => c.toUpperCase())}
            </div>
           
          </div>

          {/* Divider */}
          {/* <div
            style={{
              height: "1px",
              background: "#e6e6e6",
              margin: "0px 0",
              borderRadius: "1px",
            }}
          /> */}

          {/* Attributes */}
          {/* <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  background: "linear-gradient(90deg, #d68e08ff, #b77306ff)",
                  color: "white",
                  fontSize: "12px",
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: "12px",
                }}
              >
                Top 1% Ancestors
              </span>
              <span
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  lineHeight: 1.1,
                }}
              >
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: "16px",
                    color: "#444444ff",
                    letterSpacing: "0.02em"

                  }}
                >
                 { pool.excellentAt ? `> ` + Number(pool.excellentAt).toLocaleString() : "—"}

                </span>

                <span
                  style={{
                    fontSize: "11px",
                    color: "#8a8a8a",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  ancestors
                </span>
              </span>

            </div> */}
            {/* <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  background: "linear-gradient(90deg, #1e8008ff, #035402ff)",
                  color: "white",
                  fontSize: "12px",
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: "12px",
                }}
              >
                Best of The Best
              </span>
              <span style={{ fontWeight: 500, color: "#444" }}>{pool.excellentAt} 
              </span>
            </div> */}
          {/* </div> */}
        </div>
      );
    })}
  </div>
</div>




      {/* RESULTS */}
      <div style={{ width: "100%", maxWidth: "720px", marginTop: "40px" }}>
        <ResultsPanel
          key={queryMeta?.query + resultsResetTrigger}
          parsed={parsed}
          loading={loading}
          error={error}
          responseRaw={responseRaw}
          queryMeta={queryMeta}
          user={user}
          userId={userId}
          aboutClicked={aboutClicked}       // new
          setAboutClicked={setAboutClicked} // new
          resetTrigger={resultsResetTrigger}
        />
      </div>

    </Container>

    {/* Recharge Modal */}
      <Modal show={showRechargeModal} onHide={() => {
        setShowRechargeModal(false);
        setError("");
        setCustomAmount("");
        setPaymentMethod("stripe");
      }} centered className="rounded-4" contentClassName="border-0 shadow-lg">
        <Modal.Header closeButton className="border-0 px-4 pt-4">
          <Modal.Title className="fw-semibold text-center w-100"
            style={{
              fontSize: "18px",
              letterSpacing: "-0.3px",
              color: "#3b3b3bff"
            }}
          >
            
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 pb-4 pt-0">
          <div className="d-flex flex-column align-items-center gap-3 w-100">
            <div className="w-100" style={{ maxWidth: "320px" }}>
              <p
                style={{
                  fontSize: "16px",
                  color: "#374151",
                  textAlign: "center",
                  maxWidth: "320px",
                  marginBottom: "26px"
                }}
              >
                 If this tool helped you think differently or gain clarity,
                  you can support its development..
              </p>
              <input
                type="number"
                className="form-control text-center"
                style={{
                  maxWidth: "320px",
                  borderRadius: "14px",
                  padding: "14px 16px",
                  fontSize: "14px",
                  border: "1px solid #e5e7eb",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                  transition: "all 0.3s ease"
                }}
                placeholder="Enter amount (minimum $7.0)"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                min="7.0"
              />
            </div>



            <div className="w-100" style={{ maxWidth: "320px" }}>
              {/* <Form.Label className="text-muted mb-1" style={{ fontSize: "12px" }}>
                Choose Payment Method
              </Form.Label> */}
              <select
                className="form-select"
                style={{
                  borderRadius: "12px",
                  padding: "10px 14px",
                  fontSize: "15px",
                  border: "1px solid #dee2e6"
                }}
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <option value="stripe">💳 Pay with Stripe</option>
              </select>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            <Button
              variant="success"
              className="rounded-pill px-4 py-2 text-white border-0 ms-3 mb-0 mt-3 shadow-sm"
              style={{
                padding: "10px 24px",
                background: "linear-gradient(120deg, #0dc300ff, #065600ff)",
                transition: "opacity 0.3s",
              }}
              onClick={() => handleCheckout(Number(customAmount))}
              disabled={!customAmount || Number(customAmount) <= 6.99 || paymentMethod === "naira"}>
              Buy ${customAmount || 0} Credits
            </Button>
          </div>
        </Modal.Body>
      </Modal>



    
  </>
   }
        />

         

        {/* DIARY PAGE */}
        <Route path="/diary" element={<DiaryScreen user={user}/>} />

      </Routes>
    </Router>
    
);


}

function buildPrompt(query, totalAncestors, detectedRegions = [], specificInputs = []) {
  const q = query.replace(/["']/g, "");
  let ancestryContext = "";


if (specificInputs.length && detectedRegions.length) {
    ancestryContext = `
The user has provided these specific ancestral identities:
- ${specificInputs.join(", ")}

These should be treated as **cultural–spiritual and historical lenses**, not biological determinism.

These identities map to the following broader ancestral regions:
- ${detectedRegions.join(", ")}

You MUST:
- Use the *specific identities* (e.g. Esan, Edo State, Punjab, Kikuyu, Sicily) to enrich cultural, historical, spiritual, traditional or behavioral framing.
- Use the *broader regions* (e.g. West Africa, Europe, South Asia) to anchor and bias *statistical inference* about human trait distribution.
- Balance all reasoning with scientific, psychological and anthropological understanding.
- Never imply genetic destiny; treat ancestral influence as cultural, environmental, behavioral, and symbolic.
    `;
  } 
  
  else if (detectedRegions.length) {
    ancestryContext = `
The user’s ancestral roots map to these regions:
- ${detectedRegions.join(", ")}

Use these regions to bias the cultural and statistical modeling of the trait.
Do not assume biological determinism — focus on cultural, historical, spiritual or environmental influence.
    `;
  }

  else if (specificInputs.length) {
    ancestryContext = `
The user has provided specific ancestral identities:
- ${specificInputs.join(", ")}

You were unable to map them to a broad region.
Use a global ancestral model but still honor the cultural or spiritual hints implied by the specific inputs.
    `;
  }

  else {
    ancestryContext = `
No ancestral information was provided.
Use a global ancestral model.
    `;
  }


  return `
You are the world’s most precise statistical–biological inference engine.

${ancestryContext}  

A user asks about the possibility of manifesting this trait: "${q}".
i.e how many ancestors had the ability or were able to make this desire come true NOT how many had the desire.
if the trait is an actual trait then estimate how many ancestors had this trait.
Since its about ancestors, modern inventions or concepts must be re-framed into their historical equivalents for example Education or technology has always existed in some form.
Your task: estimate how many of their ${totalAncestors} ancestors had this trait at 3 levels, depending on its possibility.

But FIRST, determine what *type* of trait it is.

────────────────────────────────────────
### STEP 1 — CLASSIFY THE INPUT
Classify the query into ONE of these categories:

1. **Biological Ability / Physical Capability**  
   (e.g., regenerate limbs, grow wings, breathe underwater)

2. **Health Condition / Medical Phenomenon**  
   (e.g., spontaneous remission, unusual healing cases)

3. **Skill / Trainable Ability**  
   (e.g., painting, public speaking, endurance)

4. **Personality Trait / Psychological Tendency**  
   (e.g., calmness, introspection)

5. **Abstract / Metaphorical / Spiritual**  
   (e.g., “mysteries of my mind,” “activate inner fire”)

6. **Desire / Event**  
   (e.g., “want my girlfriend back,” “find lost treasure”)
   - If it's a modern concept, reframe it into a historical equivalent.
   - It it's a modern concept, never it's impossible; reframe it.

7. **Gibberish / Meaningless / Random Input** 
   (e.g., random characters, nonsensical phrases)
     - If "${q}" is meaningless Return 0 for goodAt, excellentAt, and percentages.
     - state the reason for returning 0

8. **Impossible Trait**  
   - Physically and spiritually impossible for humans  
   - Not observed historically  
   - Not supported by biology  

────────────────────────────────────────
### STEP 2 — HANDLE EACH CATEGORY

#### If the trait is **biologically impossible**:

1. FIRST, check **all known mystical, spiritual, or traditional beliefs** that might allow this trait.
2. IF such beliefs exist:
    - Provide ancestor estimates based on these beliefs.
    - Numbers should be small and conservative (e.g., 1–5% of totalAncestors or less).
    - Ensure excellentAt ≤ goodAt ≤ totalAncestors.
    - Include reasoning (6–10 sentences) explaining the spiritual/traditional context, why the numbers are conservative, and how this informs ancestor estimates.
3. ONLY if the trait is impossible **both scientifically and spiritually**:
    - Return 0 for goodAt, excellentAt, and percentages.
    - Reasoning must clearly explain why no ancestors could possess it.

#### If the trait is a desire or event and not a skill/ability e.g "want my girl friend back"
- estimate the ability to make this desire or event come true, through behavior, personality or circumstance.
- DO NOT estimate the number of ancestors that desired the event itself.
- provide clear reasoning. 

#### If the trait is a desire or event and not a skill/ability e.g "want my girl friend back"
- estimate the ability to make this desire or event come true, through behavior, personality or circumstance.
- DO NOT estimate the number of ancestors that desired the event itself.
- provide clear reasoning. 

#### If the word used as trait is giberish, meaningless or you can't tell the meaning
- return 0 for goodAt, excellentAt, and percentages.
- state the reason for returning 0

#### If the trait is **a rare biological or medical phenomenon**:
- Check if any documented cases exist in history or medicine.
- Estimate plausible global incidence.
- Use those incidence rates to estimate ancestor counts.

#### If the trait is a **skill or psychological trait**:
- Map to a validated domain (Big Five, cognitive skills, emotional regulation).
- Use normal curves, known population distributions or close proxies.
- If no direct data, infer from human ability distributions (normal curves).
- Weight your reasoning according to the **detected ancestral regions**.


────────────────────────────────────────
### STEP 3 — PRODUCE NUMERICAL ESTIMATES
You must create these fields (integers):


- goodAt: strong or above average (top ~25%) for this trait
- excellentAt: top 1–5% ancestors strongest in this trait


All must obey: excellentAt ≤ goodAt ≤ totalAncestors.

────────────────────────────────────────
### STEP 4 — OUTPUT FORMAT (REQUIRED)
Return ONLY this JSON:

{
  "totalAncestors": ${totalAncestors},
  "trait": "${q}",
  "traitRemix": "<another version or rearrangement of the words used in trait so it can be used correctly in this sentence: 'How Many Ancestors + {trait}?'. No need to rearrange or re-edit if trait already works. Don't add 'How Many Ancestors' part. If it's a desire, frame like it happended already. If its a too modern frame as the old version of it for example 'App' in old days can be replaced with 'Idea'>,",
  "goodAt": <integer>,
  "excellentAt": <integer>,
  "percentages": {
    "good": <number>,
    "excellent": <number>
  },
  "ancestryUsed": {
    "specific": ${JSON.stringify(specificInputs)},
    "regions": ${JSON.stringify(detectedRegions)}
  },
  "reasoning": "< Note: The user is looking to know they have what it takes to manifest these traits in them. Give 4–6 clear, easy to understand sentences explaining: (1) The ancestors you see here were exceptionally successful at (2) evidence or lack thereof, (3) inference logic, (4) why the result is conservative and consistent, including spiritual/mystical considerations if used.>"
}

IMPORTANT:
- Do NOT hallucinate research.  
- Always output estimates if spiritual/mystical traditions exist, even if biologically impossible.  
- Only return zero if the trait is impossible **both scientifically and spiritually**.  
- Output only the JSON.  
- Watch out for misleading questions like "Never had sex" because if they are ancestors, they must have had sex.
- No extra text.

Begin now.
`;
}




